import React from 'react';
import { Input, Select } from 'antd';
import { SearchSelect } from '../components/SearchSelect';
import { type ValueEditorProps, type ValidationResult } from 'react-querybuilder';
import * as Styled from './styled';
import { isTagField, getFieldSchema, allowMultiSelection, checkError } from '../utils';
import {
  InputType,
  type QueryBuilderFieldSchema,
  type QueryBuilderRule,
  type QueryBuilderContext,
} from '../types';
import { ErrorMessage } from '../styled';
import { DataSource } from '@components/filter/components/utils';

const isEmptyQueryBuilderRuleValue = (value: QueryBuilderRule['value']) =>
  !value ||
  !value.length ||
  (value.length === 1 && ['', null, undefined].includes(value[0]?.value));

const formatData = (data: Record<string, any> | Record<string, any>[]) => {
  if (!data) return [];

  if (!Array.isArray(data)) {
    return [
      {
        value: data.value,
        label: data.label ?? String(data.value ?? ''),
      },
    ];
  } else {
    return data.map((it) => ({
      value: it.value,
      label: it.label ?? String(it.value ?? ''),
    }));
  }
};

const genPlaceholder = (dataSource: DataSource, multipleMode: boolean) => {
  return multipleMode ? 'Input one or more values.' : 'Input one value.';
};

const DimensionValue = (props: ValueEditorProps) => {
  const { path, schema, context, validation } = props;
  const rule = props.rule as QueryBuilderRule;
  const isTag = isTagField(rule.field);
  const tagKey = (rule as QueryBuilderRule).key as string;
  const fieldSchema = getFieldSchema(rule.field, schema.fields as QueryBuilderFieldSchema[]);

  const { dataSource, onValueChange, showError } = context as QueryBuilderContext;
  const error = checkError(validation as ValidationResult, 'value');
  const multipleMode = allowMultiSelection(rule.operator);
  const isEmptyValue = isEmptyQueryBuilderRuleValue(rule.value);
  const placeholder = genPlaceholder(dataSource, multipleMode);

  const errorMsg = showError && error ? error.message : '';
  const className = `dimension-value-input ${errorMsg ? 'error-input' : ''}`;

  let children = null as React.ReactNode;

  switch (fieldSchema?.inputType) {
    case InputType.Input:
      if (multipleMode) {
        children = (
          <SearchSelect
            placeholder={placeholder}
            key={fieldSchema.name}
            enableFreeInput
            optionFilterProp="label"
            className={className}
            mode={multipleMode ? 'tags' : undefined}
            value={isEmptyValue ? [] : rule.value}
            notFoundContent={null}
            onChange={(data) => onValueChange(formatData(data), path, fieldSchema)}
          />
        );
      } else {
        children = (
          <Input
            placeholder={placeholder}
            className={className}
            key={fieldSchema.name}
            value={isEmptyValue ? '' : rule.value[0].value}
            onChange={(e) => {
              const { value } = e.target;
              onValueChange(formatData({ value, label: value }), path, fieldSchema);
            }}
          />
        );
      }
      break;

    case InputType.Select:
      children = (
        <Select
          allowClear
          showSearch
          labelInValue
          placeholder={placeholder}
          className={className}
          mode={multipleMode ? 'multiple' : undefined}
          key={fieldSchema.name}
          optionFilterProp="label"
          value={isEmptyValue ? [] : rule.value}
          options={fieldSchema.valueOptions}
          notFoundContent={null}
          onChange={(data) => {
            onValueChange(formatData(data), path, fieldSchema);
          }}
        />
      );
      break;

    case InputType.SearchInput:
      children = (
        <SearchSelect
          enableFreeInput
          placeholder={placeholder}
          key={fieldSchema.name}
          className={className}
          mode={multipleMode ? 'tags' : undefined}
          value={isEmptyValue ? [] : rule.value}
          notFoundContent={fieldSchema.autoCompletion ? undefined : null}
          optionFilterProp="label"
          onRemoteSearch={(value) => {
            if (isTag) {
              return fieldSchema.getTagValueOptions?.(value, tagKey);
            } else {
              return fieldSchema.getValueOptions?.(value);
            }
          }}
          onChange={(data) => {
            onValueChange(formatData(data), path, fieldSchema);
          }}
        />
      );
      break;

    case InputType.SearchSelect:
      children = (
        <SearchSelect
          key={fieldSchema.name}
          className={className}
          mode={multipleMode ? 'multiple' : undefined}
          value={isEmptyValue ? [] : rule.value}
          optionFilterProp="label"
          placeholder={placeholder}
          notFoundContent={fieldSchema.autoCompletion ? undefined : null}
          onRemoteSearch={(value) => {
            if (isTag) {
              return fieldSchema.getTagValueOptions?.(value, tagKey);
            } else {
              return fieldSchema.getValueOptions?.(value);
            }
          }}
          onChange={(data) => onValueChange(formatData(data), path, fieldSchema)}
        />
      );
      break;
    default:
      children = (
        <SearchSelect
          disabled
          mode="tags"
          optionFilterProp="label"
          className={className}
          value={isEmptyValue ? [] : rule.value}
          notFoundContent={null}
        />
      );
  }

  return (
    <Styled.Container>
      {children}
      {errorMsg && <ErrorMessage className="error-message">{errorMsg}</ErrorMessage>}
    </Styled.Container>
  );
};

export default React.memo(DimensionValue);
